export type MediaProgram = {
  id: string;
  frTitle: string;
  arTitle: string;
  zghTitle: string;
  subject_fr?: string;
  subject_ar?: string;
  subject_zgh?: string;
  channel: string;
  type: "tv" | "radio" | "web";
  periodicity: "quotidien" | "hebdo" | "mensuel" | "ponctuel";
  dateFormatted_fr: string;
  dateFormatted_ar: string;
  dateFormatted_zgh: string;
  time: string;
  dateIso: string; // Used for timeline calculations
  durationMinutes: number;
  guests_fr: string;
  guests_ar: string;
  guests_zgh: string;
  description_fr: string;
  description_ar: string;
  description_zgh: string;
  host_fr?: string;
  host_ar?: string;
  host_zgh?: string;
  replayUrl?: string;
  youtubeUrl?: string;
  liveUrl?: string;
  posterUrl?: string;
  published?: boolean;
  status: "pending" | "approved" | "rejected";
  partnerName?: string;
  mediaLogo?: string;
  socialLinks?: {
    youtube?: string;
    facebook?: string;
    tiktok?: string;
    instagram?: string;
    x?: string;
    website?: string;
  };
  likesCount?: number;
  isModified?: boolean;
  createdAt?: string;
  updatedAt?: string;
  daysOfWeek?: string[];
  autoPublish?: boolean;
  autoPeriodicity?: boolean;
};

// MOCK DATA : generating dates relative to standard current time to keep demonstrations meaningful.
// In a real database, dateIso would just be the precise timestamp of the broadcast.

const today = new Date();
const pad = (n: number) => n.toString().padStart(2, "0");

const getDateIso = (dayOffset: number, hours: number, minutes: number) => {
  const d = new Date(today);
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
};

export const sampleProgramsInfo: MediaProgram[] = [
  {
    id: "prog_live_now",
    frTitle: "Avec Ramdani (مع الرمضاني)",
    arTitle: "مع الرمضاني",
    zghTitle: "ⴷⵉⴷⵙ ⵔⵎⴹⴰⵏⵉ",
    subject_fr: "Épisode : Bilan à mi-mandat et défis de 2026",
    subject_ar: "حلقة: الحصيلة المرحلية وتحديات 2026",
    subject_zgh: "ⵜⴰⵙⴷⴷⵉⵜ : ⵜⴰⵢⴰⴼⵓⵜ ⵏ ⵓⵣⴳⵏ ⵏ ⵜⵏⴱⴰⴹⵜ ⴷ ⵉⵏⴰⵥⴰⵕⵏ ⵏ 2026",
    channel: "2M",
    type: "tv",
    periodicity: "hebdo",
    dateFormatted_fr: "Aujourd'hui",
    dateFormatted_ar: "اليوم",
    dateFormatted_zgh: "ⴰⵙⵙⴰ",
    time: "21:30",
    dateIso: getDateIso(0, 21, 30),
    durationMinutes: 90,
    guests_fr: "Mustapha Baitas, Porte-parole du gouvernement",
    guests_ar: "مصطفى بايتاس، الناطق الرسمي باسم الحكومة",
    guests_zgh: "ⵎⵚⵟⴰⴼⴰ ⴱⴰⵢⵜⴰⵙ, ⴰⵎⵙⵉⵡⵍ ⵓⵏⵚⵉⴱ ⵏ ⵜⵏⴱⴰⴹⵜ",
    description_fr: "Le grand débat hebdomadaire pour discuter des réalisations gouvernementales et des perspectives électorales.",
    description_ar: "النقاش الأسبوعي الكبير لمناقشة الإنجازات الحكومية والآفاق الانتخابية.",
    description_zgh: "ⴰⵎⵙⴳⴷⴰⵍ ⴰⵏⵎⵍⴰⵙⵙ ⵎⵇⵇⵓⵔⵏ ⵉ ⵓⵙⴰⵔⴰⴳ ⵅⴼ ⵜⵡⵓⵔⵉⵡⵉⵏ ⵏ ⵜⵏⴱⴰⴹⵜ.",
    host_fr: "Ridouane Erramdani",
    host_ar: "رضوان الرمضاني",
    host_zgh: "ⵔⵉⴹⵡⴰⵏ ⵔⵎⴹⴰⵏⵉ",
    status: "approved",
    published: true,
  },
  {
    id: "prog_imminent",
    frTitle: "Point à la Ligne (نقطة إلى السطر)",
    arTitle: "نقطة إلى السطر",
    zghTitle: "ⵜⵉⵇⵇⵉⴹⵜ ⵙ ⵓⵣⵔⵉⴳ",
    subject_fr: "Épisode : Le Code de la Famille, vers un consensus ?",
    subject_ar: "حلقة: مدونة الأسرة، نحو إجماع وطني؟",
    subject_zgh: "ⵜⴰⵙⴷⴷⵉⵜ : ⴰⵙⵍⴳⵏ ⵏ ⵜⵡⵊⴰ, ⵖⵔ ⵓⵎⵙⴰⵙⴰ ?",
    channel: "Al Aoula",
    type: "tv",
    periodicity: "mensuel",
    dateFormatted_fr: "Aujourd'hui",
    dateFormatted_ar: "اليوم",
    dateFormatted_zgh: "ⴰⵙⵙⴰ",
    time: "22:15",
    dateIso: getDateIso(0, 22, 15),
    durationMinutes: 120,
    guests_fr: "Représentants PPS, PJD & Militantes associatives",
    guests_ar: "ممثلو التقدم والاشتراكية، العدالة والتنمية، وناشطات جمعويات",
    guests_zgh: "ⵉⵎⵙⵜⴰⵢⵏ ⵏ PPS, PJD ⴷ ⵜⵎⵖⴰⵔⵉⵏ ⵏ ⵜⵎⵙⵎⵓⵏⵉⵏ",
    description_fr: "Débat de fond sur la réforme de la Moudawana et les défis législatifs de 2026.",
    description_ar: "نقاش جوهري حول إصلاح المدونة والتحديات التشريعية لعام 2026.",
    description_zgh: "ⴰⵎⵙⴳⴷⴰⵍ ⵅⴼ ⵓⵙⵖⵓⴷⵓ ⵏ ⵎⵓⴷⴰⵡⴰⵏⴰ.",
    host_fr: "Sabah Bendaoud",
    host_ar: "صباح بنداود",
    host_zgh: "ⵚⴰⴱⴰⵃ ⴱⵏⴷⴰⵡⴷ",
    status: "approved",
    published: true,
  },
  {
    id: "prog_upcoming_1",
    frTitle: "L'Info en Face",
    arTitle: "المعلومة في الواجهة",
    zghTitle: "ⵉⵏⵖⵎⵉⵙⵏ ⴳ ⵓⴷⵎ",
    subject_fr: "Épisode : Investissements et souveraineté industrielle",
    subject_ar: "حلقة: الاستثمارات والسيادة الصناعية",
    subject_zgh: "ⵜⴰⵙⴷⴷⵉⵜ : ⵉⵙⵙⴰⵔⵓⵜⵏ ⴷ ⵜⵏⴱⴰⴹⵜ ⵜⴰⵎⴳⴳⵓⵔⵜ",
    channel: "Matin TV",
    type: "web",
    periodicity: "quotidien",
    dateFormatted_fr: "Demain",
    dateFormatted_ar: "غدا",
    dateFormatted_zgh: "ⴰⵣⴽⴽⴰ",
    time: "19:00",
    dateIso: getDateIso(1, 19, 0),
    durationMinutes: 45,
    guests_fr: "Younes Sekkouri & Experts économiques",
    guests_ar: "يونس السكوري وخبراء اقتصاديون",
    guests_zgh: "ⵢⵓⵏⵙ ⵙⴽⴽⵓⵔⵉ ⴷ ⵉⵎⵙⵙⵏⵜⵉⵜⵏ ⵏ ⵜⴷⵎⵙⴰ",
    description_fr: "Analyse des stratégies économiques du gouvernement actuel.",
    description_ar: "تحليل الاستراتيجيات الاقتصادية للحكومة الحالية.",
    description_zgh: "ⴰⵙⴼⵔⴽ ⵏ ⵜⵙⵔⵜⴰⵢ ⵜⵉⴷⵎⵙⴰⵏⵉⵏ.",
    host_fr: "Rachid Hallaouy",
    status: "approved",
    published: true,
  },
  {
    id: "prog_upcoming_2",
    frTitle: "Confidences de Presse",
    arTitle: "أسرار الصحافة",
    zghTitle: "ⵉⵙⵔⵉⵔⵏ ⵏ ⵜⵖⵎⵙⴰ",
    subject_fr: "Épisode : Préparatifs pour les Législatives 2026",
    subject_ar: "حلقة: الاستعدادات لتشريعيات 2026",
    subject_zgh: "ⵜⴰⵙⴷⴷⵉⵜ : ⵉⵙⵓⵊⴰⴷⵏ ⵉ ⵉⵙⵜⴰⵢⵏ 2026",
    channel: "2M",
    type: "tv",
    periodicity: "hebdo",
    dateFormatted_fr: "Dans 2 jours",
    dateFormatted_ar: "بعد يومين",
    dateFormatted_zgh: "ⴷⴼⴼⵉⵔ ⵙⵉⵏ ⵡⵓⵙⵙⴰⵏ",
    time: "18:00",
    dateIso: getDateIso(2, 18, 0),
    durationMinutes: 60,
    guests_fr: "Driss Lachgar, Premier secrétaire USFP",
    guests_ar: "إدريس لشكر، الكاتب الأول لحزب الاتحاد الاشتراكي",
    guests_zgh: "ⴷⵔⵉⵙ ⵍⵛⴳⵔ, ⴰⵎⴰⵔⵉ ⴰⵎⵣⵡⴰⵔⵓ ⵏ USFP",
    description_fr: "Face aux journalistes pour décrypter les stratégies d'alliances électorales.",
    description_ar: "في مواجهة الصحفيين لتحليل استراتيجيات التحالفات الانتخابية.",
    description_zgh: "ⴰⵎⵙⴳⴷⴰⵍ ⴷ ⵉⵏⵖⵎⴰⵙⵏ ⵅⴼ ⵜⵙⵔⵜⵉⵜ ⵏ ⵓⵎⴰⵇⵇⴰⵏ.",
    host_fr: "Abdellah Tourabi",
    host_ar: "عبد الله الترابي",
    status: "approved",
    published: true,
  },
  {
    id: "prog_past_1",
    frTitle: "Bi Doun Haraj (بدون حرج)",
    arTitle: "بدون حرج",
    zghTitle: "ⵡⴰⵔ ⴰⴽⵔⴼ",
    subject_fr: "Épisode : Sécheresse, l'urgence nationale",
    subject_ar: "حلقة: الجفاف، حالة طوارئ وطنية",
    subject_zgh: "ⵜⴰⵙⴷⴷⵉⵜ : ⵜⴰⵖⴰⵔⵜ, ⵜⴰⵖⴰⵡⵙⴰ ⵜⴰⵏⴰⵎⵓⵔⵜ",
    channel: "Medi1 TV",
    type: "tv",
    periodicity: "hebdo",
    dateFormatted_fr: "La semaine dernière",
    dateFormatted_ar: "الأسبوع الماضي",
    dateFormatted_zgh: "ⵉⵎⴰⵍⴰⵙⵙ ⵉⵣⵔⵉⵏ",
    time: "20:30",
    dateIso: getDateIso(-3, 20, 30),
    durationMinutes: 90,
    guests_fr: "Ministre de l'Équipement et de l'Eau",
    guests_ar: "وزير التجهيز والماء",
    guests_zgh: "ⴰⵎⴰⵡⵙⴰⵙ ⵏ ⵓⵎⴰⵡⴰ ⴷ ⵡⴰⵎⴰⵏ",
    description_fr: "Débat citoyen et institutionnel sur le stress hydrique et les barrages.",
    description_ar: "نقاش مواطن ومؤسساتي حول الإجهاد المائي والسدود.",
    description_zgh: "ⴰⵎⵙⴳⴷⴰⵍ ⵅⴼ ⵓⵥⵍⴰⵢ ⵏ ⵡⴰⵎⴰⵏ ⴷ ⵉⴳⵍⵎⴰⵎⵏ.",
    host_fr: "Youssef Belhaissi",
    replayUrl: "https://youtube.com/example",
    status: "approved",
    published: true,
  },
  {
    id: "prog_pending_example_1",
    frTitle: "Campus Débat : Éducation",
    arTitle: "نقاش الحرم الجامعي: التعليم",
    zghTitle: "ⴰⵎⵙⴳⴷⴰⵍ ⵏ ⵜⵙⴷⴰⵡⵉⵜ: ⴰⵙⵙⵍⵎⴷ",
    channel: "Hit Radio",
    type: "radio",
    periodicity: "quotidien",
    dateFormatted_fr: "Après-demain",
    dateFormatted_ar: "بعد غد",
    dateFormatted_zgh: "ⴷⴼⴼⵉⵔ ⴰⵣⴽⴽⴰ",
    time: "15:00",
    dateIso: getDateIso(2, 15, 0),
    durationMinutes: 60,
    guests_fr: "Politiciens et Étudiants",
    guests_ar: "سياسيون وطلاب",
    guests_zgh: "ⵉⵎⵙⵔⵜⴰⵏⵏ ⴷ ⵉⵏⵍⵎⴰⴷⵏ",
    description_fr: "Projet de nouvelle émission avec la participation étudiante.",
    description_ar: "مشروع برنامج جديد بمشاركة طلابية.",
    description_zgh: "ⴰⵙⵏⴼⴰⵔ ⵏ ⵓⵖⴰⵡⴰⵙ ⴰⵎⴰⵢⵏⵓ ⵙ ⵜⴷⵔⴰⵡⵜ ⵏ ⵉⵏⵍⵎⴰⴷⵏ.",
    status: "pending",
    published: false,
    partnerName: "Hit Radio Group",
  }
];


type ProgramStatus = "live" | "imminent" | "finished" | "upcoming" | "past";

export const getProgramStatus = (dateIso: string, durationMinutes: number): ProgramStatus => {
  const current = new Date();
  const start = new Date(dateIso);
  const end = new Date(start.getTime() + durationMinutes * 60000);
  const diffMs = start.getTime() - current.getTime();

  // If current time is between start and end => Live
  if (current >= start && current <= end) {
    return "live";
  }

  // If it's within 2 hours after end
  if (current > end && current <= new Date(end.getTime() + 2 * 60 * 60 * 1000)) {
    return "finished";
  }

  // If it already passed more than 2 hours
  if (current > new Date(end.getTime() + 2 * 60 * 60 * 1000)) {
    return "past";
  }

  // If it starts in less than 60 minutes
  if (diffMs > 0 && diffMs <= 60 * 60 * 1000) {
    return "imminent";
  }

  return "upcoming";
};

export const sortProgramsChronological = (a: MediaProgram, b: MediaProgram) => {
  const statusA = getProgramStatus(a.dateIso, a.durationMinutes);
  const statusB = getProgramStatus(b.dateIso, b.durationMinutes);

  const getPriority = (status: ProgramStatus) => {
    if (status === 'live') return 1;
    if (status === 'imminent') return 2;
    if (status === 'finished') return 3;
    if (status === 'upcoming') return 4;
    if (status === 'past') return 5;
    return 6;
  };

  const pA = getPriority(statusA);
  const pB = getPriority(statusB);

  // 1. Sort by Priority (Live -> Imminent -> Upcoming -> Past)
  if (pA !== pB) return pA - pB;

  // 2. Within the same priority group:
  if (statusA === 'upcoming' || statusA === 'imminent') {
    // For future, 'closest to now' means Ascending date (a - b)
    return new Date(a.dateIso).getTime() - new Date(b.dateIso).getTime();
  } else {
    // For past, 'closest to now' (most recent) means Descending date (b - a)
    return new Date(b.dateIso).getTime() - new Date(a.dateIso).getTime();
  }
};
